import { insertImage } from '@platejs/media';
import { ImagePlugin } from '@platejs/media/react';
import { BaseMentionPlugin } from '@platejs/mention';
import type { TElement } from 'platejs';
import { deserializeHtml } from 'platejs';
import { createPlatePlugin } from 'platejs/react';

export const PastePlugin = createPlatePlugin({
  key: 'paste',
  options: {
    onPasteMention: (_data: any) => true as boolean,
    removeFormattings: false,
    onStripCidImages: undefined as (() => void) | undefined,
    isUploadingInlineImages: false,
    allowedImageTypes: [] as { type: string; extension: string }[],
    allowPublicImageUpload: undefined as
      | 'required'
      | 'allowed'
      | 'disallowed'
      | undefined,
    pendingUploadCallback: undefined as
      | ((uploadAsPubliclyAccessible: boolean) => void)
      | undefined,
  },
  handlers: {
    onPaste: ({ editor, event, getOptions, setOption }) => {
      try {
        const clipboardData = event.clipboardData.getData('text/plain');

        if (clipboardData.includes('$KADORichTextEditor')) {
          const data = JSON.parse(clipboardData);

          if (data['$KADORichTextEditor']) {
            event.preventDefault();

            const value = data['$KADORichTextEditor'];

            const mentionType = BaseMentionPlugin.key;
            const insertSpaceAfterMention = editor.getOption(
              BaseMentionPlugin,
              'insertSpaceAfterMention',
            );

            const { onPasteMention } = getOptions();

            if (!onPasteMention(value)) {
              return true;
            }

            editor.tf.withoutNormalizing(() => {
              editor.tf.insertNodes({
                type: mentionType,
                children: [{ text: '' }],
                value,
              } as TElement);

              editor.tf.move({ unit: 'offset' });

              if (insertSpaceAfterMention) {
                editor.tf.insertText(' ');
              }
            });

            return true;
          }
        }

        const { removeFormattings } = getOptions();

        if (removeFormattings) {
          editor.tf.withoutNormalizing(() => {
            event.preventDefault();
            editor.tf.insertText(
              event.clipboardData.getData('text/plain').replace(/\n|\r/g, ''),
            );

            return true;
          });
          return;
        }

        // Upload images pasted directly as file
        const files = event.clipboardData.files;
        if (files && files.length > 0) {
          const { allowedImageTypes } = getOptions();

          const allImageFiles = Array.from(files).filter((file) =>
            file.type.startsWith('image/'),
          );

          if (allImageFiles.length > 0) {
            const imageFiles = allImageFiles.filter((file) => {
              if (allowedImageTypes.length === 0) return true;

              return allowedImageTypes.some(
                (allowed) => allowed.type === file.type,
              );
            });

            event.preventDefault();

            if (imageFiles.length === 0) return true;

            const { onUploadImage } = editor.getOptions(ImagePlugin) as {
              onUploadImage?: (
                fileOrData: File | string,
                opt: { uploadAsPubliclyAccessible?: boolean },
              ) => Promise<string>;
            };

            const { allowPublicImageUpload } = getOptions();

            // When not 'disallowed', show to the accessibility modal
            if (allowPublicImageUpload !== 'disallowed') {
              setOption('pendingUploadCallback', (isPublic: boolean) => {
                // Since the plugin handler is synchronous, the upload must operate as "fire-and-forget".
                // However, a dedicated loading spinner visualizes the process for the user and prevents concurrent changes.
                void (async () => {
                  setOption('isUploadingInlineImages', true);
                  try {
                    await Promise.all(
                      imageFiles.map(async (file) => {
                        if (onUploadImage) {
                          try {
                            const uploadedUrl = await onUploadImage(file, {
                              uploadAsPubliclyAccessible: isPublic,
                            });
                            insertImage(editor, uploadedUrl);
                          } catch {
                            // Upload failed, skip image.
                          }
                        }
                      }),
                    );
                  } finally {
                    setOption('isUploadingInlineImages', false);
                  }
                })();
              });
              return true;
            }

            // Since the plugin handler is synchronous, the upload must operate as "fire-and-forget".
            // However, a dedicated loading spinner visualizes the process for the user and prevents concurrent changes.
            void (async () => {
              setOption('isUploadingInlineImages', true);
              try {
                await Promise.all(
                  imageFiles.map(async (file) => {
                    if (onUploadImage) {
                      try {
                        const uploadedUrl = await onUploadImage(file, {
                          uploadAsPubliclyAccessible: false,
                        });
                        insertImage(editor, uploadedUrl);
                      } catch {
                        // Upload failed, skip image.
                      }
                    }
                  }),
                );
              } finally {
                setOption('isUploadingInlineImages', false);
              }
            })();

            return true;
          }
        }

        const htmlData = event.clipboardData.getData('text/html');

        const hasCidImages = Boolean(htmlData && /src="cid:/i.test(htmlData));
        const hasDataImages = Boolean(
          htmlData && /src="data:image\//i.test(htmlData),
        );
        if (hasCidImages || hasDataImages) {
          const doc = new DOMParser().parseFromString(htmlData, 'text/html');

          let strippedCount = 0;
          doc
            .querySelectorAll('img[src^="cid:"], img[src^="CID:"]')
            .forEach((img) => {
              img.remove();
              strippedCount++;
            });

          event.preventDefault();

          if (hasDataImages) {
            const { allowedImageTypes } = getOptions();

            const allInlineImages = Array.from(
              doc.querySelectorAll('img'),
            ).filter((img) =>
              img.getAttribute('src')?.toLowerCase().startsWith('data:image/'),
            );

            const inlineImages = allInlineImages.filter((img) => {
              const src = img.getAttribute('src');
              if (!src) return false;

              if (allowedImageTypes.length === 0) return true;

              // Extract MIME type from data URL (e.g., "data:image/png;base64,...")
              const mimeMatch = src.match(/^data:([^;,]+)/);
              const mimeType = mimeMatch?.[1];

              if (!mimeType) return false;

              return allowedImageTypes.some(
                (allowed) => allowed.type === mimeType,
              );
            });

            allInlineImages.forEach((img) => {
              if (!inlineImages.includes(img)) {
                img.remove();
              }
            });

            if (inlineImages.length > 0) {
              const { onUploadImage } = editor.getOptions(ImagePlugin) as {
                onUploadImage?: (
                  fileOrData: File | string,
                  opt: { uploadAsPubliclyAccessible?: boolean },
                ) => Promise<string>;
              };

              const { allowPublicImageUpload } = getOptions();

              // When not 'disallowed', show to the accessibility modal
              if (allowPublicImageUpload !== 'disallowed') {
                setOption('pendingUploadCallback', (isPublic: boolean) => {
                  // Since the plugin handler is synchronous, the upload must operate as "fire-and-forget".
                  // However, a dedicated loading spinner visualizes the process for the user and prevents concurrent changes.
                  void (async () => {
                    setOption('isUploadingInlineImages', true);
                    try {
                      await Promise.all(
                        inlineImages.map(async (img) => {
                          const src = img.getAttribute('src');
                          if (!src) return;

                          if (onUploadImage) {
                            try {
                              const uploadedUrl = await onUploadImage(src, {
                                uploadAsPubliclyAccessible: isPublic,
                              });
                              img.setAttribute('src', uploadedUrl);
                            } catch {
                              img.remove();
                            }
                          } else {
                            img.remove();
                          }
                        }),
                      );

                      const fragment = deserializeHtml(editor, {
                        element: doc.body,
                      });
                      if (fragment.length > 0) {
                        editor.tf.insertFragment(fragment);
                      }

                      if (strippedCount > 0) {
                        const { onStripCidImages } = getOptions();
                        onStripCidImages?.();
                      }
                    } finally {
                      setOption('isUploadingInlineImages', false);
                    }
                  })();
                });
                return true;
              }

              // Since the plugin handler is synchronous, the upload must operate as "fire-and-forget".
              // However, a dedicated loading spinner visualizes the process for the user and prevents concurrent changes.
              void (async () => {
                setOption('isUploadingInlineImages', true);
                try {
                  await Promise.all(
                    inlineImages.map(async (img) => {
                      const src = img.getAttribute('src');
                      if (!src) return;

                      if (onUploadImage) {
                        try {
                          const uploadedUrl = await onUploadImage(src, {
                            uploadAsPubliclyAccessible: false,
                          });
                          img.setAttribute('src', uploadedUrl);
                        } catch {
                          img.remove();
                        }
                      } else {
                        img.remove();
                      }
                    }),
                  );

                  const fragment = deserializeHtml(editor, {
                    element: doc.body,
                  });
                  if (fragment.length > 0) {
                    editor.tf.insertFragment(fragment);
                  }

                  if (strippedCount > 0) {
                    const { onStripCidImages } = getOptions();
                    onStripCidImages?.();
                  }
                } finally {
                  setOption('isUploadingInlineImages', false);
                }
              })();

              return true;
            }
          }

          const fragment = deserializeHtml(editor, { element: doc.body });
          if (fragment.length > 0) {
            editor.tf.insertFragment(fragment);
          }

          if (strippedCount > 0) {
            const { onStripCidImages } = getOptions();
            onStripCidImages?.();
          }

          return true;
        }
      } catch {
        // ignore
      }
    },
  },
});
